import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { AppRoutes } from '@/constants/appRoutes'
import { SocialAnalysisItem } from '@/pages/Reports/components/SocialAnalysisItem'
import {
  selectSelectedTeam,
  selectSelectedWorkStation,
} from '@/store/api/Users/Users.selector'
import { useGetWorkspaceScopedReportsQuery } from '@/store/api/Workspace/Workspace.query'

const HOME_REPORTS_FETCH_LIMIT = 12

const FALLBACK_ROW_HEIGHT_PX = 72

export const HomeReportsSection = () => {
  const { t } = useTranslation()

  const selectedWorkStation = useSelector(selectSelectedWorkStation)
  const selectedTeam = useSelector(selectSelectedTeam)

  const skip = !selectedWorkStation?._id || !selectedTeam?._id

  const { data, isFetching } = useGetWorkspaceScopedReportsQuery(
    {
      workspaceId: selectedWorkStation?._id ?? '',
      teamId: selectedTeam?._id ?? '',
      page: 1,
      pageSize: HOME_REPORTS_FETCH_LIMIT,
      // Ask for reports rather than asking for a mixed page and dropping half
      // of it: face checks share this endpoint, and once they were the most
      // recent thing, nothing was left to show.
      excludeFaceChecks: true,
    },
    { skip },
  )

  const reports = data?.items ?? []
  const isLoading = isFetching && !data

  const listRef = useRef<HTMLDivElement>(null)
  const itemsWrapperRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(HOME_REPORTS_FETCH_LIMIT)

  useLayoutEffect(() => {
    const node = listRef.current
    if (!node) return

    const recalc = () => {
      const firstItem = itemsWrapperRef.current
        ?.firstElementChild as HTMLElement | null
      const rowHeight = firstItem?.offsetHeight || FALLBACK_ROW_HEIGHT_PX
      const fit = Math.max(1, Math.floor(node.clientHeight / rowHeight))
      setVisibleCount(Math.min(fit, HOME_REPORTS_FETCH_LIMIT))
    }

    recalc()
    const observer = new ResizeObserver(recalc)
    observer.observe(node)
    return () => observer.disconnect()
  }, [reports.length])

  const visibleReports = reports.slice(0, visibleCount)

  return (
    <section className="w-full max-w-[1200px] flex flex-col flex-1 min-h-0">
      <div className="bg-baseColorHighlight rounded-[24px] px-4 py-6 flex flex-col gap-5 flex-1 min-h-0">
        <div className="flex items-center justify-between px-3">
          <h2 className="text-xl font-medium text-neutralColor">
            {t('dashboard.reportsScreen.homeSectionTitle')}
          </h2>
          <Link
            to={AppRoutes.reports}
            className="text-xs font-medium text-neutralColor60 hover:text-neutralColor transition-colors"
          >
            {t('dashboard.reportsScreen.viewAll')}
          </Link>
        </div>

        <div
          ref={listRef}
          className="flex-1 min-h-0 flex flex-col overflow-hidden"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <LottieSpinner />
            </div>
          ) : reports.length === 0 ? (
            <HomeReportsEmptyState />
          ) : (
            <div ref={itemsWrapperRef} className="flex flex-col">
              {visibleReports.map(
                (report) =>
                  report.type === 'socialcheck' && (
                    <SocialAnalysisItem
                      key={report.data.id}
                      data={report.data}
                      showActions={false}
                    />
                  ),
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

const HomeReportsEmptyState = () => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-full px-6 text-center">
      <ProhibitionIcon />
      <div className="flex flex-col gap-1">
        <p className="text-base font-medium text-neutralColor">
          {t('dashboard.reportsScreen.homeEmptyTitle')}
        </p>
        <p className="text-sm text-neutralColor60">
          {t('dashboard.reportsScreen.homeEmptySubtitle')}
        </p>
      </div>
    </div>
  )
}

const ProhibitionIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="text-neutralColor60"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <line
      x1="5.5"
      y1="5.5"
      x2="18.5"
      y2="18.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
)
